import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

/* fetch all users of selected client */
export const fetchAllUsers = createAsyncThunk(
  "users",
  async ({client_id, page, limit, search, field}, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/users?client_id=${client_id}&page=${page}&limit=${limit}${search ? `&value=${search}` : ""}${field ? `&field=${field}` : ""}${search && field ? `&operator=equals` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* create user */
export const createUser = createAsyncThunk('user/create',async (userData, { rejectWithValue }) => {
  try {
    const response = await apiPath.post(`/api/v1.0/users/create`, userData);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* User Lists of selected client */
export const fetchUserLists = createAsyncThunk('user/list',async (clientId, { rejectWithValue }) => {
  try {
    const response = await apiPath.get(`/api/v1.0/users/lists?client_id=${clientId}`);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* update Password */
export const updatePassword = createAsyncThunk('user/update-password',async ({id,userData}, { rejectWithValue }) => {
  try {
    const response = await apiPath.patch(`/api/v1.0/users/${id}/update-password`,userData);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* delete user */
export const deleteUser = createAsyncThunk(
  "user/delete",
  async ( id ,{ rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/users/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* user detail */
export const userDetail = createAsyncThunk(
  "user/detail",
  async ( id ,{ rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/users/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* update role */
export const updateUser = createAsyncThunk(
  "user/update",
  async ({ id, userData },{ rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/users/${id}`, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


/* update user profile */
export const updateUserProfile = createAsyncThunk(
  "user/update-profile",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/users/${id}`, userData);
      return response.data;
    }
    catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* delete user profile picture */
export const deleteUserProfilePicture = createAsyncThunk(
  "user/delete-profile-picture",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.patch(`/api/v1.0/users/${id}`, { profile_picture: null });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


/* STP user */
/* fetch STP records */
export const fetchSTPRecords = createAsyncThunk(
  "user/fetchSTPRecords",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/records/stp?client_id=${clientId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/* ----------------- Refresh Info Thunks ----------------- */
export const fetchUserRefreshInfo = createAsyncThunk(
  "user/fetchRefreshInfo",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/users/${userId}/refresh-info`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserRefreshInfo = createAsyncThunk(
  "user/updateRefreshInfo",
  async ({ userId, refreshData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.patch(`/api/v1.0/users/${userId}/refresh-info`, refreshData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* User Slice */
const userSlice = createSlice({
    name: 'user',
    initialState: {
      users: [],
      loading: false,
      userDetail: {},
      error: null,
      totalRecord: 0,
      stpList: [],
      profilePictureLoading: false, 
      profilePictureError: null,
      // 👇 ADDED: Refresh info state
      refreshInfo: {
        data: null,
        loading: false,
        error: null
      },
      // 👇 ADDED: Store defaults per user
      userRefreshDefaults: {}
    },
    reducers: {
      // Optional: You can add a reducer to clear profile picture errors
      clearProfilePictureError: (state) => {
        state.profilePictureError = null;
      },
      // 👇 ADDED: Set user default refresh times
      setUserDefaultRefresh: (state, action) => {
        const { userId, defaultTimes } = action.payload;
        if (!state.userRefreshDefaults) {
          state.userRefreshDefaults = {};
        }
        state.userRefreshDefaults[userId] = {
          reports: defaultTimes,
          updated_at: new Date().toISOString()
        };
      },
      // 👇 ADDED: Clear user refresh defaults
      clearUserRefreshDefaults: (state, action) => {
        const { userId } = action.payload;
        if (state.userRefreshDefaults && state.userRefreshDefaults[userId]) {
          delete state.userRefreshDefaults[userId];
        }
      }
    }, 
    extraReducers: (builder) => {
        builder
        /* fetch All users of selected client @start */
        .addCase(fetchAllUsers.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchAllUsers.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.users = action.payload.users;
            state.totalRecord = action.payload.totalRecord
        })
        .addCase(fetchAllUsers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        })

        /* Create User */
        .addCase(createUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(createUser.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          state.users.push(action.payload);
          state.totalRecord = state.totalRecord+1
        })
        .addCase(createUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })

        /* fetch all user lists according to client ID */
        .addCase(fetchUserLists.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchUserLists.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          state.users = action.payload;
        })
        .addCase(fetchUserLists.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        
        .addCase(userDetail.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(userDetail.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          state.userDetail = action.payload.details;
        })
        .addCase(userDetail.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })

        /* update user password */
        .addCase(updatePassword.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updatePassword.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
        })
        .addCase(updatePassword.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        
        /* Delete User */
        .addCase(deleteUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(deleteUser.fulfilled, (state, action) => {
          state.loading = false;
          state.users = state.users.filter(
            (u) => u.id !== action.payload
            
          );
          state.totalRecord = state.totalRecord-1
        })
        .addCase(deleteUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        
        /* Update User */
          .addCase(updateUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateUser.fulfilled, (state, action) => {
          state.error = null;
          state.loading = false;
          const index = state.users.findIndex(
            u => u.id === action.payload.id
          );
          if (index !== -1) {
            state.users[index] = action.payload;
          }
        })
        .addCase(updateUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        
        /* Update User Profile */
        .addCase(updateUserProfile.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateUserProfile.fulfilled, (state, action) => {
          state.error = null;
          state.loading = false;
          // const index = state.users.findIndex(
          //   u => u.id === action.payload.id
          // );  
          // if (index !== -1) {
          //   state.users[index] = action.payload;
          // }
          state.userDetail = action.payload;
        })
        .addCase(updateUserProfile.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        
        /* Delete User Profile Picture */
        .addCase(deleteUserProfilePicture.pending, (state) => {
          state.profilePictureLoading = true;
          state.profilePictureError = null;
        })
        .addCase(deleteUserProfilePicture.fulfilled, (state, action) => {
          state.profilePictureLoading = false;
          state.profilePictureError = null;
          
          // Update userDetail with the response
          if (state.userDetail && state.userDetail.id === action.payload.id) {
            state.userDetail.profile_picture = action.payload.profile_picture;
          }
          
          // Also update in users list if the user exists there
          const userIndex = state.users.findIndex(user => user.id === action.payload.id);
          if (userIndex !== -1) {
            state.users[userIndex].profile_picture = action.payload.profile_picture;
          }
        })
        .addCase(deleteUserProfilePicture.rejected, (state, action) => {
          state.profilePictureLoading = false;
          state.profilePictureError = action.error.message;
        })
        
        .addCase(fetchSTPRecords.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchSTPRecords.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          console.log("Fetched STP Records:", action.payload);
          state.stpList = action.payload.stps || [];
        })
        .addCase(fetchSTPRecords.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        })
        /* 👇 ADDED: Fetch User Refresh Info */
        .addCase(fetchUserRefreshInfo.pending, (state) => {
          if (!state.refreshInfo) state.refreshInfo = {};
          state.refreshInfo.loading = true;
          state.refreshInfo.error = null;
        })
        .addCase(fetchUserRefreshInfo.fulfilled, (state, action) => {
          state.refreshInfo.loading = false;
          state.refreshInfo.error = null;
          if (!state.refreshInfo) state.refreshInfo = {};
          state.refreshInfo.data = action.payload;
          
          const userId = action.meta.arg;
          if (userId && action.payload?.reports) {
            if (!state.userRefreshDefaults) state.userRefreshDefaults = {};
            state.userRefreshDefaults[userId] = {
              reports: action.payload.reports,
              updated_at: new Date().toISOString()
            };
          }
        })
        .addCase(fetchUserRefreshInfo.rejected, (state, action) => {
          state.refreshInfo.loading = false;
          state.refreshInfo.error = action.payload || action.error.message;
        })
        /* 👇 ADDED: Update User Refresh Info */
        .addCase(updateUserRefreshInfo.pending, (state) => {
          if (!state.refreshInfo) state.refreshInfo = {};
          state.refreshInfo.loading = true;
          state.refreshInfo.error = null;
        })
        .addCase(updateUserRefreshInfo.fulfilled, (state, action) => {
          state.refreshInfo.loading = false;
          state.refreshInfo.error = null;
          if (!state.refreshInfo) state.refreshInfo = {};
          state.refreshInfo.data = action.payload;
          
          const userId = action.meta.arg.userId;
          if (userId && action.payload?.reports) {
            if (!state.userRefreshDefaults) state.userRefreshDefaults = {};
            state.userRefreshDefaults[userId] = {
              reports: action.payload.reports,
              updated_at: new Date().toISOString()
            };
          }
        })
        .addCase(updateUserRefreshInfo.rejected, (state, action) => {
          state.refreshInfo.loading = false;
          state.refreshInfo.error = action.payload || action.error.message;
        });
    },
});

export const { clearProfilePictureError, setUserDefaultRefresh, clearUserRefreshDefaults } = userSlice.actions;
export default userSlice.reducer;